import React, { useEffect } from "react";
import Footer from "../components/Footer";

const Section = ({ children, className = "" }) => (
  <section className={`py-8 sm:py-10 md:py-12 ${className}`}>
    <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-5 md:px-6">{children}</div>
  </section>
);

function Kicker({ children }) {
  return <div className="text-[11px] sm:text-[12px] font-semibold tracking-wider uppercase text-[#7a6f67]">{children}</div>;
}

function Stat({ value, label }) {
  return (
    <div className="text-center py-3 sm:py-4">
      <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a1a1a]">{value}</div>
      <div className="text-[12px] sm:text-[13px] text-[#666] mt-1">{label}</div>
    </div>
  );
}

function TimelineItem({ year, title, text }) {
  return (
    <div className="relative pl-6 sm:pl-8 pb-4 sm:pb-0">
      <div className="absolute left-0 top-1.5 h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-[#1a1a1a]" />
      <div className="text-xs sm:text-sm font-medium text-[#7a6f67] mb-1">{year}</div>
      <div className="font-semibold text-sm sm:text-base text-gray-900 mb-1">{title}</div>
      <p className="text-[#555] text-[13px] sm:text-[14px] leading-relaxed">{text}</p>
    </div>
  );
}

function USP({ icon, title, text }) {
  return (
    <div className="p-4 sm:p-5 border border-[#eee] rounded-lg hover:shadow-md transition-shadow">
      <div className="text-2xl sm:text-3xl mb-2">{icon}</div>
      <div className="mt-2 font-semibold text-base sm:text-lg text-gray-900">{title}</div>
      <p className="text-[13px] sm:text-[14px] text-[#555] mt-1.5 leading-relaxed">{text}</p>
    </div>
  );
}

// Unused component - kept for future use
// function TeamCard({ name, role, img }) {
//   return (
//     <div className="text-center">
//       <img src={img} alt={name} className="mx-auto w-28 h-28 rounded-full object-cover border border-[#eee]" />
//       <div className="mt-3 font-semibold">{name}</div>
//       <div className="text-[13px] text-[#666]">{role}</div>
//     </div>
//   );
// }

export default function About() {
  useEffect(() => {
    document.title = "Rólunk – XL bútor";
  }, []);

  return (
    <main className="bg-white text-[#1a1a1a]">
      {/* HERO */}
      <section className="relative py-4 sm:py-0">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-5 md:px-6">
          <div className="relative overflow-hidden rounded-lg sm:rounded-xl">
            <img
              src="/images/Weboldalképei/513502099_23889229397400221_4984633965961444247_n.jpg"
              alt="Rólunk hero kép"
              className="w-full h-[40vh] sm:h-[45vh] md:h-[55vh] lg:h-[60vh] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            <div className="absolute left-4 right-4 sm:left-6 sm:right-auto bottom-4 sm:bottom-6 bg-white/95 backdrop-blur-sm px-4 py-4 sm:px-6 sm:py-5 max-w-full sm:max-w-xl rounded-lg shadow-xl">
              <Kicker>Az XL bútor története</Kicker>
              <h1 className="font-serif text-[22px] sm:text-[28px] md:text-[36px] font-semibold leading-tight mt-1 sm:mt-2 text-gray-900">
                Egyedi bútorok, professzionális kivitelezés – 1989 óta
              </h1>
              <p className="text-[13px] sm:text-[14px] text-[#555] mt-2 leading-relaxed">
                Több mint három évtizedes tapasztalattal készítjük egyedi bútoraidat saját asztalos műhelyünkben.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSZIÓ + STATOK */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start">
          <div className="md:col-span-2">
            <Kicker>Küldetésünk</Kicker>
            <h2 className="text-[20px] sm:text-[24px] md:text-[28px] font-semibold mt-2 text-gray-900">Egyedi bútorok az Ön elképzelése szerint</h2>
            <p className="text-[14px] sm:text-[15px] text-[#444] leading-6 sm:leading-7 mt-2 sm:mt-3">
              Fő tevékenységünk az egyedi bútor gyártás, amely a megrendelőink maximális testreszabási igényei szerint készül. 3D tervezéssel kezdjük, majd saját asztalos műhelyünkben készítjük el mindent – a tervezéstől a kivitelezésig minden egy helyen történik. Ezen kívül széles választékban kínálunk szőnyegeket, lakberendezési eszközöket és konyhafelszerelési tárgyakat.
            </p>
            <div className="mt-5 sm:mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              <Stat value="35+" label="év tapasztalat" />
              <Stat value="1500 m²" label="üzlet + műhely" />
              <Stat value="3D" label="tervezés" />
              <Stat value="2018" label="asztalos műhely" />
            </div>
          </div>
          <div className="mt-6 md:mt-0">
            <img 
              src="https://picsum.photos/seed/about-side/900/1200" 
              alt="XL bútor műhely" 
              className="w-full aspect-[4/5] object-cover rounded-lg shadow-md border border-[#eee]" 
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* TÖRTÉNET IDŐVONAL */}
      <Section className="bg-[#faf8f6]">
        <Kicker>Történetünk</Kicker>
        <h3 className="text-[18px] sm:text-[22px] md:text-[24px] font-semibold mt-2 text-gray-900">Több mint 35 év folyamatos fejlődés</h3>
        <div className="mt-5 sm:mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <div className="space-y-5 sm:space-y-6">
            <TimelineItem 
              year="1989" 
              title="A kezdetek" 
              text="Vállalkozásunkat használt bútor és háztartási felszerelések kereskedelmével kezdtük egy 500 m² bérelt üzletben." 
            />
            <TimelineItem 
              year="1990-es évek" 
              title="Folyamatos bővülés" 
              text="Átváltottunk új bútor, szőnyeg, függöny, háztartási és konyhai berendezések forgalmazására. Választékunk folyamatosan bővült." 
            />
            <TimelineItem 
              year="2007" 
              title="Saját ingatlan és új üzlet" 
              text="Megépítettük az új üzletünket, amely kezdetben 500 m² alapterülettel indult, majd folyamatosan bővítettük." 
            />
          </div>
          <div className="space-y-5 sm:space-y-6">
            <TimelineItem 
              year="2010-es évek" 
              title="Bővítés 1500 m²-re" 
              text="Az üzlet teljes alapterületét 1500 m²-re növeltük, hogy még szélesebb választékot és kényelmesebb vásárlási élményt nyújthassunk." 
            />
            <TimelineItem 
              year="2018" 
              title="Saját asztalos műhely" 
              text="Elkészült modern asztalos műhelyünk lapszabászattal és bútorlap raktárakkal együtt. Ettől kezdve minden egyedi bútor a saját műhelyünkben készül." 
            />
            <TimelineItem 
              year="Ma" 
              title="Komplett szolgáltatás" 
              text="3D tervezés, egyedi bútor gyártás, szőnyeg és lakberendezési eszközök forgalmazása, valamint szállítás és költöztetés – minden egy helyen." 
            />
          </div>
        </div>
      </Section>

      {/* MIÉRT MI (USP-k) */}
      <Section>
        <Kicker>Szolgáltatásaink</Kicker>
        <h3 className="text-[18px] sm:text-[22px] md:text-[24px] font-semibold mt-2 text-gray-900">Amit biztosan kapsz tőlünk</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mt-5 sm:mt-6">
          <USP icon="🛠️" title="Egyedi bútor gyártás" text="3D tervezés és teljes körű kivitelezés saját műhelyünkben, maximális testreszabással." />
          <USP icon="🏪" title="Széles választék" text="Új bútor, szőnyeg, függöny, lakberendezési és konyhafelszerelési eszközök egy helyen." />
          <USP icon="🚚" title="Szállítás és költöztetés" text="Bútorszállítás, költöztetés és fuvarozás is szolgáltatásaink között." />
          <USP icon="📐" title="Lapszabászat" text="Modern lapszabászat és bútorlap raktár a gyors és precíz munkavégzésért." />
        </div>
      </Section>

      {/* Footer */}
      <Footer />
    </main>
  );
}
